import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BUTTON_CONTINUE,
  BUTTON_RECHARGE_BALANCE,
  CAMPAIGN_CREATED,
  CAMPAIGN_UPDATED,
  ERROR,
  ERROR_ON_AD_GROUP_ID,
  ERROR_ON_AD_ID_OF_AD_GROUP_ID,
  ERROR_ON_ASSETS_GROUP_ID,
} from "../../../i18n/keysTranslations";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import { logoPlatform, openLink } from "../../../utils/string";
import { BORDER_RADIUS, GOOGLE, META, TIKTOK } from "../../../utils/constants";
import { errorCodeToText, ERRORS_BILLING } from "../../../utils/errors";
import Button from "../../../components/Buttons/Button";
import {
  getBillingUrlGoogleAds,
  getBillingUrlMeta,
  getBillingUrlTikTokBusiness,
  getIntegrationKey,
} from "../../../actions/getters";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import Alert from "@mui/material/Alert";
import linkifyHtml from "linkify-html";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";

const ModalErrorsCampaigns = ({
  modalOpen,
  onCloseModal,
  campaign,
  from = "create",
  successCampaigns = [],
  failedCampaigns = [],
  errors = {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const integrations = useSelectorApp((state) => state?.user?.integrations);

  const commonSxContainer = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    border: `${INPUT_COLOR_BORDER} 1px solid`,
    p: 2,
    borderRadius: BORDER_RADIUS,
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={async () => {
        onCloseModal();
      }}
      scroll="paper"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Campañas exitosas */}
        {successCampaigns.map((platform) => (
          <Box
            key={platform}
            sx={{
              ...commonSxContainer,
              flexDirection: {
                xs: "column",
                md: "row",
              },
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  ".MuiAvatar-img": {
                    objectFit: "contain",
                  },
                }}
                src={logoPlatform(platform)}
                variant="square"
              />
              <Typography variant="h6">{t(platform)}</Typography>
            </Box>
            <Alert severity="success">
              {t(from === "create" ? CAMPAIGN_CREATED : CAMPAIGN_UPDATED)}
            </Alert>
          </Box>
        ))}

        {/* Campañas fallidas */}
        {failedCampaigns.map((platform) => {
          const error = errors[platform];
          const code = error?.code;
          const platformData = campaign?.platformsData?.[platform];
          const pageID = platformData?.pageID;
          const integrationID = platformData?.integrationID;
          const integrationKey = getIntegrationKey(platform, true);
          const integrationData =
            integrations?.[integrationKey]?.[integrationID];
          const page = integrationData?.pages?.[pageID];
          const businessID = page?.businessID;

          const message =
            errorCodeToText(code) === code
              ? error?.message
              : errorCodeToText(code, {
                pageID,
                businessID,
              });

          const additionalData = error?.additionalData || {};
          const scope = additionalData?.scope || "";
          const isDifferentScopeCampaign = scope !== "campaign" && scope;

          const getTextScope = () => {
            const assetsGroupID = additionalData?.assetsGroupID;
            const adGroupID = additionalData?.adGroupID;
            const adID = additionalData?.adID;

            if (!adGroupID && !adID && !assetsGroupID) return "";

            if (assetsGroupID) {
              const assetsGroupIndex = campaign?.assetsGroups?.findIndex(
                (assetsGroup) => assetsGroup.id === assetsGroupID
              );
              return t(ERROR_ON_ASSETS_GROUP_ID, {
                assetsGroupID: assetsGroupIndex + 1,
              });
            }

            if (adID && adGroupID) {
              const adGroupIndex = campaign?.adsGroups?.findIndex(
                (adGroup) => adGroup.id === adGroupID
              );
              const ads = campaign?.adsGroups?.[adGroupIndex]?.ads;
              const adIndex = ads?.findIndex((ad) => ad.id === adID);
              return t(ERROR_ON_AD_ID_OF_AD_GROUP_ID, {
                adGroupID: adGroupIndex + 1,
                adID: adIndex + 1,
              });
            }

            if (adGroupID) {
              const adGroupIndex = campaign?.adsGroups?.findIndex(
                (adGroup) => adGroup.id === adGroupID
              );
              return t(ERROR_ON_AD_GROUP_ID, {
                adGroupID: adGroupIndex + 1,
              });
            }
          };

          return (
            <Box
              key={platform}
              sx={{
                ...commonSxContainer,
                alignItems: "flex-start",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    md: "row",
                  },
                  alignItems: "flex-start",
                  width: "100%",
                  gap: 1,
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      ".MuiAvatar-img": {
                        objectFit: "contain",
                      },
                    }}
                    src={logoPlatform(platform)}
                    variant="square"
                  />
                  <Typography variant="h6">{t(platform)}</Typography>
                </Box>
                <Alert severity="error">{t(ERROR)}</Alert>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    "& *": {
                      p: "revert",
                      m: "revert",
                      listStyle: "revert",
                      textDecoration: "revert",
                    },
                    "& a": {
                      textDecoration: "underline",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: linkifyHtml(message, {
                      rel: "noopener noreferrer",
                      target: "_blank",
                      style: "color: #00A3FF",
                    }),
                  }}
                />
                {isDifferentScopeCampaign && (
                  <Typography variant="body1" sx={{ my: 1 }}>
                    {`(${getTextScope()})`}
                  </Typography>
                )}
              </Box>

              {ERRORS_BILLING.includes(code) && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                  }}
                >
                  <Button
                    onClick={() => {
                      let url;
                      if (platform === META) {
                        url = dispatch(
                          getBillingUrlMeta(platformData.adAccountID)
                        );
                      } else if (platform === TIKTOK) {
                        url = dispatch(
                          getBillingUrlTikTokBusiness(platformData.adAccountID)
                        );
                      } else if (platform === GOOGLE) {
                        url = dispatch(
                          getBillingUrlGoogleAds(platformData.adAccountID)
                        );
                      }
                      openLink(url);
                    }}
                  >
                    {t(BUTTON_RECHARGE_BALANCE)}
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <DialogActions sx={{ p: 0, mt: 1 }}>
        <ContainerModalActions
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_CONTINUE,
            onClick: onCloseModal,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalErrorsCampaigns;
